/**
 * In-dialog account selection for the per-account tool dialogs (Filters,
 * Screener, Spam). Each dialog owns which account it operates on — seeded from an
 * optional default (the sidebar's active/primary account) and kept valid as the
 * account list loads or the default changes. Replaces the old shared "Account
 * for tools" picker that used to live in the sidebar.
 */

import { useEffect, useState } from 'react';
import type { EmailConnection } from '@/lib/api';
import { useEmailConnections } from './useConnectAccount';

export interface DialogAccount {
  accounts: EmailConnection[];
  selectedAccountId: string | undefined;
  setSelectedAccountId: (accountId: string) => void;
}

export function useDialogAccount(defaultAccountId: string | undefined): DialogAccount {
  const { data: connections } = useEmailConnections();
  const accounts = connections ?? [];
  const [selectedAccountId, setSelectedAccountId] = useState<string | undefined>(defaultAccountId);

  useEffect(() => {
    if (accounts.length === 0) return;
    const valid = selectedAccountId && accounts.some((a) => a.accountId === selectedAccountId);
    if (valid) return;
    setSelectedAccountId(
      defaultAccountId && accounts.some((a) => a.accountId === defaultAccountId)
        ? defaultAccountId
        : (accounts.find((a) => a.isPrimary)?.accountId ?? accounts[0]?.accountId),
    );
  }, [accounts, defaultAccountId, selectedAccountId]);

  return { accounts, selectedAccountId, setSelectedAccountId };
}
